import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Briefcase, MapPin, Clock } from 'lucide-react';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { useRTLArrow } from '@/hooks/useRTL';
import { useContent } from '@/hooks/useContent';

gsap.registerPlugin(ScrollTrigger);

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
}

const jobs: Job[] = [
  {
    id: 1,
    title: 'Medical Laboratory Technician',
    department: 'Laboratory',
    location: 'Cairo',
    type: 'Full-time',
    description: 'We are looking for an experienced medical laboratory technician to join our team.',
  },
  {
    id: 2,
    title: 'Customer Service Representative',
    department: 'Customer Service',
    location: 'Cairo',
    type: 'Full-time',
    description: 'Join our customer service team and help patients with their inquiries and appointments.',
  },
  {
    id: 3,
    title: 'Quality Control Specialist',
    department: 'Quality Assurance',
    location: 'Cairo',
    type: 'Full-time',
    description: 'Ensure the highest quality standards in our laboratory operations.',
  },
];

const Careers = () => {
  const { t } = useContent();
  const sectionRef = useRef<HTMLDivElement>(null);
  const { getArrow } = useRTLArrow();
  const ArrowIcon = getArrow('right');

  useEffect(() => {
    document.title = 'Careers - ElhamyLabs';
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-purple-light dark:from-dark-primary dark:via-dark-secondary">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              {t('common.careers')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('careers.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Jobs Section */}
      <section ref={sectionRef} className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-center text-light-text dark:text-dark-text">{t('careers.openPositions')}</h2>
          <div className="space-y-6">
            {jobs.map((job, index) => (
              <ScrollReveal key={job.id} delay={index * 0.1}>
                <div className="p-8 bg-light-secondary dark:bg-dark-secondary rounded-xl hover:shadow-xl transition-all duration-300">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-2 text-light-text dark:text-dark-text">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-light-text-secondary dark:text-dark-text-secondary mb-4">
                        <div className="flex items-center gap-2">
                          <Briefcase size={16} />
                          {job.department}
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin size={16} />
                          {job.location}
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock size={16} />
                          {job.type}
                        </div>
                      </div>
                      <p className="text-light-text-secondary dark:text-dark-text-secondary">
                        {job.description}
                      </p>
                    </div>
                    <button className="px-6 py-3 bg-brand-orange-base text-white rounded-lg hover:bg-brand-orange-light transition-colors font-semibold flex items-center gap-2 whitespace-nowrap">
                      {t('common.applyNow')}
                      <ArrowIcon size={20} />
                    </button>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;

